/**
 * One order state machine for every channel. The channel (web / pos / kiosk /
 * phone) is a field on the order, never a separate flow — a POS sale and a web
 * sale differ in how they reach `paid`, not in what an order is.
 */
export const ORDER_STATUSES = [
    'draft', // being built (cart, POS ticket in progress)
    'pending_payment',
    'paid',
    'fulfilling', // being picked/packed, or device being prepared
    'shipped',
    'ready_for_pickup',
    'completed',
    'cancelled',
    'refunded',
];
export const ORDER_CHANNELS = ['web', 'pos', 'kiosk', 'phone'];
/**
 * How the customer gets what they bought. Distinct from status: an order can
 * be `paid` and still be either collected at the counter or posted out, and
 * which one decides whether `shipped` or `ready_for_pickup` comes next.
 *
 * `none` is a plan-only order — a service plan is activated on the customer's
 * account, so there is nothing to collect or post out. Appended last,
 * deliberately, for the same reason as `sim` on `PRODUCT_TYPES` below: a
 * Postgres enum's `ADD VALUE` always appends.
 */
export const FULFILLMENT_METHODS = ['pickup', 'shipping', 'none'];
/**
 * Truly final — nothing follows. `completed` is deliberately NOT here: a
 * completed order can still be refunded when a customer returns a device.
 */
export const TERMINAL_ORDER_STATUSES = ['cancelled', 'refunded'];
/** Statuses that close an order for day-to-day work (no further staff action expected). */
export const CLOSED_ORDER_STATUSES = ['completed', 'cancelled', 'refunded'];
export const ORDER_TRANSITIONS = {
    draft: ['pending_payment', 'paid', 'cancelled'], // POS cash sale can go straight to paid
    pending_payment: ['paid', 'cancelled'],
    paid: ['fulfilling', 'refunded', 'cancelled'],
    fulfilling: ['shipped', 'ready_for_pickup', 'completed', 'refunded'],
    shipped: ['completed', 'refunded'],
    ready_for_pickup: ['completed', 'refunded'],
    completed: ['refunded'],
    cancelled: [],
    refunded: [],
};
export function canTransitionOrder(from, to) {
    return ORDER_TRANSITIONS[from].includes(to);
}
/**
 * The base map allows both `shipped` and `ready_for_pickup` out of
 * `fulfilling` because either channel can reach that state — but a given
 * order only ever goes out one way. This narrows the base map down to the
 * step that matches how *this* order is actually being fulfilled.
 */
export function nextOrderStatusesFor(from, fulfillmentMethod) {
    return ORDER_TRANSITIONS[from].filter((to) => {
        if (to === 'shipped')
            return fulfillmentMethod === 'shipping';
        if (to === 'ready_for_pickup')
            return fulfillmentMethod === 'pickup';
        return true;
    });
}
export function canTransitionOrderFor(from, to, fulfillmentMethod) {
    return nextOrderStatusesFor(from, fulfillmentMethod).includes(to);
}
/**
 * Statuses in which stock and serialized units are committed to the order.
 *
 * `draft` belongs here: stock is reserved the moment an order is created, which
 * is what stops two people buying the last phone. Leaving it out would mean a
 * cancelled draft never released its reservation, and the shelf would slowly
 * fill with stock the system thinks is spoken for.
 */
export const ORDER_STATUSES_HOLDING_STOCK = [
    'draft',
    'pending_payment',
    'paid',
    'fulfilling',
    'shipped',
    'ready_for_pickup',
    'completed',
];
/**
 * Statuses in which an order's money has been collected and kept — the set a
 * cost-of-goods-sold report counts as real revenue. `refunded` is
 * deliberately excluded even though it can follow `paid`/`completed`: on a
 * refund the goods physically return to stock (the unit goes back to
 * `sellable`/`returned`, counted stock gets a `return` movement), so counting
 * its revenue without netting the return back out would overstate sales.
 * `cancelled` is excluded for the same reason it holds no stock.
 */
export const REVENUE_ORDER_STATUSES = [
    'paid',
    'fulfilling',
    'shipped',
    'ready_for_pickup',
    'completed',
];
/**
 * Statuses an item-level return (t0946) can be taken from — derived, rather
 * than a second hand-maintained list, so it can never drift from
 * `ORDER_TRANSITIONS`: exactly the statuses the whole-order `refunded` move
 * already accepts, since a partial return is the same money/stock event as
 * a full refund, just scoped to fewer lines.
 */
export const RETURNABLE_ORDER_STATUSES = ORDER_STATUSES.filter((status) => ORDER_TRANSITIONS[status].includes('refunded'));
export const STOCK_MOVEMENT_REASONS = [
    'receive',
    'sale',
    'return',
    'adjustment',
    'transfer_in',
    'transfer_out',
    'damage',
    /** Parts pulled from a scrapped serialized unit rather than bought in —
     *  see `sourceUnitId` on `adjustStockSchema`. */
    'salvage',
    /** A counted part fitted to a specific serialized unit (t1139) — see the
     *  `component*` columns on `inventory_costs`. Negative when fitted, positive
     *  when the add-on is removed and the part goes back to stock. */
    'component',
    /** Counted stock sent back to the vendor as damaged (t1154) — negative when
     *  the return is opened, positive if it is cancelled before shipping. */
    'vendor_return',
    /** Counted stock taken for the office (t1223) — negative; its cost moves out of
     *  inventory into a paid expense (see `adjustStock`). */
    'office_use',
];
/**
 * `service` carries no inventory at all — repair labour, a port-in, a filter
 * recheck. It sells and invoices like anything else, but nothing leaves a
 * shelf, so it is always available.
 *
 * New types are appended last, deliberately (`sim`, then `digital_license`): a Postgres enum's `ADD VALUE` always
 * appends, and admin's product-type sort follows this declared order — an
 * insert in the middle here would silently disagree with the database.
 */
export const PRODUCT_TYPES = [
    'serialized',
    'stock',
    'plan',
    'service',
    'sim',
    'gift_card',
    'digital_license',
];
/** Product types with no stock to run out of. */
export const STOCKLESS_PRODUCT_TYPES = ['plan', 'service', 'gift_card'];
/**
 * Product types whose stock is individually-tracked units (`serialized_units`
 * rows), rather than a bare quantity — a phone by IMEI, a SIM card by ICCID,
 * a digital license by its license number.
 * Everywhere "serialized" used to mean "tracked per unit" should check this
 * instead, now that a second identifier kind exists alongside IMEI.
 */
export const UNIT_TRACKED_PRODUCT_TYPES = [
    'serialized',
    'sim',
    'digital_license',
];
export function isUnitTracked(type) {
    return UNIT_TRACKED_PRODUCT_TYPES.includes(type);
}
export const LOCATION_TYPES = ['warehouse', 'store', 'kiosk', 'repair_bench'];
//# sourceMappingURL=order-states.js.map